import { AiModelPricingSchema, type AiModelPricing, type Locale } from '@aysar/contracts';
import { type ZodType } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

/** Translates a key, with interpolation. The caller's `t`. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The four rate classes, in the order a vendor's own price list prints them.
 *
 * Derived from the schema's shape rather than restated: `AiModelPricingSchema`
 * decides what a rate is, and a list here that drifted from it would render a
 * form the server then refuses. The ORDER is this file's business — the schema
 * has none.
 */
export const AI_RATE_FIELDS = [
  'inputPerMillionUsd',
  'outputPerMillionUsd',
  'cachedInputPerMillionUsd',
  'cacheWritePerMillionUsd',
] as const satisfies readonly (keyof AiModelPricing)[];

export type AiRateField = (typeof AI_RATE_FIELDS)[number];

/** Every rate is USD, because the vendors publish in USD. Not a brand setting. */
export const AI_RATE_CURRENCY = 'USD';

/**
 * Four decimal places, and required — because the control refuses to guess.
 *
 * Gemini's cache read is `0.075`; a two-decimal field would store `0.08`, a 7%
 * error on every cached token nobody typed. Four is what the published rate
 * cards actually carry.
 */
export const AI_RATE_DECIMALS = 4;

/**
 * The rate form: one money field per class, each naming the class in words.
 *
 * `perUnit` carries "per million tokens" into the hint sentence rather than
 * beside the amount — a rate is a fraction of two units, and `$/1M` next to
 * Arabic reorders under bidi (P10.4).
 */
/** An amount said the way `MoneyInput`'s own hint says it — words, not a glyph. */
function moneyWords(amount: number, locale: Locale): string {
  return formatNumber(amount, locale, {
    style: 'currency',
    currency: AI_RATE_CURRENCY,
    currencyDisplay: 'name',
    minimumFractionDigits: 0,
    maximumFractionDigits: AI_RATE_DECIMALS,
  });
}

export function aiRateFields(
  t: Translate,
  codedDefault: AiModelPricing | null,
  locale: Locale,
): FieldDef[] {
  return AI_RATE_FIELDS.map((name) => ({
    name,
    kind: 'money' as const,
    currency: AI_RATE_CURRENCY,
    perUnit: t('rates.perUnit'),
    decimals: AI_RATE_DECIMALS,
    min: 0,
    isRequired: true,
    label: t(`rates.field.${name}`),
    // What the code ships, beside the box, so an operator can see what they are
    // deviating from — and can tell "I changed this" from "this is the default".
    // The coded default in WORDS, like every other statement of money in this
    // phase: a bare "25" under a box labelled in Arabic says nothing about
    // which currency, and pairing it with a glyph is the bidi trap P10.4 named.
    description:
      codedDefault === null
        ? t(`rates.hint.${name}`)
        : t('rates.codedDefault', { value: moneyWords(codedDefault[name], locale) }),
  }));
}

/**
 * The form's schema — the contract's own object, never a restatement of it (U3).
 *
 * Typed `ZodType<AiModelPricing, AiModelPricing>` because that is the shape
 * `DynamicForm` takes (its `TValues` is both sides), and `AiModelPricingSchema`
 * genuinely is one: every property is a required number, so parsing an
 * `AiModelPricing` returns an `AiModelPricing`. No cast — the caller passes the
 * generic explicitly and the identity holds, which a spec asserts rather than
 * trusting this comment.
 */
export function aiRateSchema(): ZodType<AiModelPricing, AiModelPricing> {
  return AiModelPricingSchema;
}

/**
 * What the form opens with.
 *
 * A row that has no rate at all (a configured model nothing prices) opens on the
 * **fallback** it is currently being charged, not on zeros: zero is a real rate
 * meaning "not charged", and pre-filling it would invite an operator to save a
 * free model by accident. `undefined` where there is not even a fallback, so the
 * boxes are honestly empty.
 */
export function aiRateInitialValues(
  rates: AiModelPricing | null,
  fallback: AiModelPricing | null,
): Partial<AiModelPricing> {
  const source = rates ?? fallback;
  if (source === null) return {};

  return {
    inputPerMillionUsd: source.inputPerMillionUsd,
    outputPerMillionUsd: source.outputPerMillionUsd,
    cachedInputPerMillionUsd: source.cachedInputPerMillionUsd,
    cacheWritePerMillionUsd: source.cacheWritePerMillionUsd,
  };
}
